/**
 * The **Insights** half of S3's Analytics & Insights panel.
 *
 * > *"A selected dataset shows its insights below the navigation. What the insights are
 * > depends on the dataset type: key statistics, value / class distributions, country
 * > comparisons, temporal trends, hotspot identification."*
 *
 * Five blocks, one request: `GET /analysis/insights` (`apps/analysis/insights.py`) resolves
 * an extent, runs the §H zonal engine over it and shapes the answer by dataset type. **This
 * component computes nothing** — a second implementation of "the mean of a raster over a
 * commune" is a second set of numbers that can disagree with the map next to it.
 *
 * ## Which blocks render
 *
 * `type` is the switch, and it is the server's:
 *
 * * **categorical** (a land-cover class raster) — the class composition is both the
 *   distribution *and* the headline, so `key_stats` is null and there is nothing to print
 *   in its place; the country comparison is the modal class's share, and the trend plots
 *   that share per epoch.
 * * **continuous** (soil carbon, rainfall, ET) — key statistics with the spread under them,
 *   the country comparison as means, and the trend over published versions where the
 *   dataset has no epochs.
 *
 * A block the answer does not carry is **absent, not empty**: a dataset with one epoch has
 * no trend, and a row of axes with no line in it reads as a chart that failed to load.
 * `omitted` is the one exception — the server says *why* it skipped the trend, and a reason
 * is worth printing where a blank is not.
 *
 * ## The two-way link
 *
 * S3: *"the panel and the map are two-way linked: a selection made on the map updates the
 * panel"*. Both directions are one field each and neither needed new machinery:
 *
 * * **panel → map** — the pane above changes the drawn dataset, and this reads it.
 * * **map → panel** — the §F5.8 cascade's `scope` and the §F5.3 click's `selectedUnit` are
 *   the extent. A click on a country or a watershed therefore rescopes the numbers, and the
 *   §F5.10 crumb, the choropleth and this panel are all describing the same unit. The scope
 *   wins where both are set: a filter is a standing instruction and a click is a glance.
 *
 * Nothing here touches the map-click popup, which S3 leaves alone.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { BarsH, Spark } from '../../components/charts';
import { fetchLayers, queryKeys, type Layer } from '../../lib/api';
import { aoiRing, fetchInsights, INSIGHTS_TOP, type Insights } from '../../lib/analysis';
import { fmtNum } from '../../lib/format';
import { selectActiveRaster, useAppStore } from '../../lib/state';
import { analysisLayerFor, seriesFor } from '../map/epochs';
import { IndicatorAnalytics } from './IndicatorAnalytics';
import {
  Block,
  ClassBars,
  ClassStack,
  ClassTrend,
  HotspotList,
  PartnershipPie,
  StatTiles,
} from './InsightsBlocks';
import {
  CLASS_TREND_TOP,
  classTrendSeries,
  partnershipSlices,
  stackedRows,
} from './insightsCharts';

/** `#sidebar` is ~20% of the viewport; its own padding leaves this for a chart. */
const CHART_WIDTH = 250;

/**
 * The hotspot ranking's unit, fixed rather than followed from the map — see `level` below.
 * It is also the server's own default since Task 5, so the two agree by construction.
 */
const HOTSPOT_LEVEL = 'watershed';

/** How many classes the distribution lists before it stops. */
const MAX_CLASSES = 6;

/** The bar colour for a block with no per-row palette of its own. */
const BAR_COLOR = '#2E7D32';

/** The partnership pie's diameter — the panel's own width, less its gutters. */
const DONUT_SIZE = 124;

/**
 * How far a country-comparison row fades when the filter is on some *other* country.
 *
 * The block deliberately keeps all four countries under a filter (see `focusIso`), which
 * left it looking identical filtered and unfiltered — the one block a reader could fairly
 * read as "the filter did nothing". Fading the three that are context, rather than dropping
 * them, keeps the comparison a comparison and still answers "which of these is mine".
 * Opacity on the whole row, so it works for the stacked composition too, where every
 * segment carries its own class colour and a substitute grey would lose the class.
 */
const CONTEXT_BAR_OPACITY = 0.32;

/**
 * The insights for a **baseline variable** — the panel's other selected-dataset case.
 *
 * A theme's default is a §D5 raster or a §C survey estimate (`themeDefaultDataset`), and
 * since the 8 Sep directive ten rail chips select nothing else. `/analysis/insights` cannot
 * answer for one: it resolves a **COG** and runs the zonal engine, and a `mb_*` row has no
 * pixels anywhere — it is 1 094 stored numbers at programme, country and watershed scope.
 *
 * **So the smallest honest change is to ask the machinery that already answers for a stored
 * value**, rather than to teach the raster endpoint about a table it does not read. That
 * machinery is `IndicatorAnalytics` (§F5.9's content, R7's section), and it already shapes
 * itself for `aggregation: 'none'`:
 *
 * * **key statistics** — the big value for the extent, with the ▲/▼ against the previous
 *   reported year (absent for a 2025-only baseline, which is the honest state, not a gap);
 * * **sample n** — on the scope line, from `indicator_values.sample_n`, because it qualifies
 *   what the number is *of*;
 * * **country comparison** — the four-country bars, the same block the raster panel draws;
 * * **the sheet** — `SiblingBars`, where this estimate sits among the others off its own
 *   workbook sheet, which is the comparison a baseline reader actually wants;
 * * **no temporal trend beyond 2025** — the spark renders only where the series has a point
 *   to plot, and the baseline reports one year, so nothing draws a line through it;
 * * **"no value at this scope"** — a survey estimated at country and watershed has nothing
 *   at region level and always will, and it says so rather than printing a bare em-dash.
 *
 * Hotspots and the class distribution are absent, and that is a property of the data: a
 * ranking of communes needs values at commune level, which the survey does not have.
 *
 * `data-instype="variable"` mirrors the raster body's `categorical`/`continuous`, so one
 * assertion asks "what kind of dataset is this panel about?" whichever it is.
 */
function VariableInsights({ id, visible }: { id: string; visible: boolean }) {
  return (
    <div className="ins-body" data-insights={id} data-instype="variable">
      <IndicatorAnalytics
        indicatorId={id}
        width={CHART_WIDTH}
        showMapButton={false}
        visible={visible}
      />
    </div>
  );
}

export function InsightsPanel() {
  const { t } = useTranslation();
  const role = useAppStore((s) => s.role);
  const layerId = useAppStore(selectActiveRaster);
  /**
   * The mapped baseline variable, where one is the selected dataset.
   *
   * The raster wins if both are somehow set: `applyThemeDefault` and `pickVariable` each
   * clear the other, so the pair is transient, and the panel must describe what is on top.
   */
  const variableId = useAppStore((s) => s.indicator);
  const scope = useAppStore((s) => s.scope);
  const selectedUnit = useAppStore((s) => s.selectedUnit);
  const country = useAppStore((s) => s.country);
  const sidebarOpen = useAppStore((s) => s.sidebarOpen);
  const aoi = useAppStore((s) => s.aoi);
  const aoiClip = useAppStore((s) => s.aoiClip);

  const { data: layers } = useQuery({ queryKey: queryKeys.layers(role), queryFn: fetchLayers });
  const layer: Layer | null = useMemo(
    () => (layers ?? []).find((l) => l.id === layerId) ?? null,
    [layers, layerId],
  );
  /** The reader's dataset — the series row, where a year of one is drawn. */
  const dataset = useMemo(
    () => seriesFor(layers, layerId) ?? layer,
    [layers, layerId, layer],
  );
  /** The row to *ask* about — the year on the slider. See `analysisLayerFor`. */
  const askId = useMemo(() => analysisLayerFor(layers, layerId), [layers, layerId]);

  // The scope wins over the click: a filter is a standing instruction, a click is a glance.
  const cascadeUnit = scope?.unitId ?? selectedUnit ?? null;

  /**
   * **The clipped ring is the extent** — client item 2, 16 Sep 2026.
   *
   * §F5.10's AOI used to be a mask on the map and nothing more: the reader drew a basin, the
   * raster clipped to it, and the panel beside it went on reporting the region the basin sits
   * in. Two different areas described as one thing, and the numbers were the ones nobody had
   * asked for. While the clip is held the ring *is* what the panel answers for.
   *
   * It is not a second filter stacked on the cascade, because the server will not read it as
   * one: `polygon` makes `insights.resolve_extent` ignore `unit` and `country` outright. So
   * the request drops both rather than sending a pair the answer does not honour — sending
   * them would put two keys on one body and make the cache lie about what it holds.
   *
   * Serialised once here: the same string is the parameter and the cache key, so a redrawn
   * ring is a different key by construction (`aoi.id` is `ringId(pts)` and says the same, but
   * the string is the thing actually sent).
   */
  const polygon = useMemo(
    () => (aoiClip && aoi ? JSON.stringify(aoiRing(aoi.pts)) : null),
    [aoiClip, aoi],
  );
  const clipped = polygon !== null;
  const unit = clipped ? null : cascadeUnit;
  const askCountry = clipped ? null : country;

  /**
   * Which of the four countries the reader has filtered to, if any — the country
   * comparison's one concession to the filter.
   *
   * That block is the only one on the panel that deliberately ignores the extent: it exists
   * to put the reader's unit *in programme context*, and narrowing a four-way comparison to
   * the one country they just chose would compare nothing (`insights.by_country` makes the
   * same argument server-side). The cost of that choice is that the block looked byte-for-
   * byte identical filtered and unfiltered, which reads as a bug rather than as a decision.
   * So the four stay and the reader's own country is the one that is not faded.
   *
   * `scope.country` before the §F1 rung: a cascade pick already carries its country, and a
   * scope always agrees with `country` anyway (`setCountry` drops a scope from elsewhere).
   */
  const focusIso = clipped ? null : scope?.country ?? (country === 'ALL' ? null : country);
  /**
   * **Hotspots rank watersheds** — client, 15 Sep 2026: *"the hotspot rankings should be by
   * watershed first"*.
   *
   * The ranking used to follow the map's reporting level, which meant the block's subject
   * changed under the reader as they moved the §F1 rung — regions, then communes, then
   * regions again — and none of those is the unit the programme plans in. A watershed is,
   * and the server ranks them under any extent (`insights.py`'s cascade: the watersheds
   * overlapping the filtered unit). The reporting level still drives the choropleth, which
   * is the map's own question and a different one.
   */
  const level = HOTSPOT_LEVEL;

  /**
   * A **display-only** layer has no raster of ours behind it (§1a rule 4) — NASA's GIBS
   * NDVI is drawn through the proxy and never stored — so there is nothing for the zonal
   * engine to read and the request would come back 422. The panel says so instead of
   * asking, which is also what the Ecosystem theme's own note says one line above.
   */
  const analysable = Boolean(
    layerId && layer && !layer.display_only && layer.has_data !== false,
  );

  const insights = useQuery({
    queryKey: queryKeys.insights(askId ?? '', unit, askCountry, polygon, level, INSIGHTS_TOP),
    queryFn: () =>
      fetchInsights({
        layer: askId as string, unit, country: askCountry, polygon, level, top: INSIGHTS_TOP,
      }),
    // A collapsed panel is 400 px off-screen; the zonal sweep behind this is the most
    // expensive read in the explorer and must not be paid for a panel nobody can see.
    enabled: Boolean(askId) && analysable && sidebarOpen,
    // The numbers behind a published dataset do not move while a reader is clicking.
    staleTime: 30 * 60 * 1000,
    retry: false,
  });

  return (
    <section
      id="sbInsights"
      aria-label={t('insights.title')}
      // Which extent the panel is answering for, for the map's own crumb and for CSS. Only
      // the drawn area needs saying: every other extent is the cascade the reader can see.
      data-insfocus={clipped ? 'aoi' : undefined}
    >
      <div className="ins-head sba-head">
        <span className="sba-title-text">{t('insights.title')}</span>
      </div>

      {!layerId && variableId ? (
        <VariableInsights id={variableId} visible={sidebarOpen} />
      ) : !layerId ? (
        <div className="sba-empty" data-insightsempty="">
          {t('insights.noDataset')}
        </div>
      ) : !analysable ? (
        <div className="sba-empty" data-insightsempty="display-only">
          {t('insights.notAnalysable', { name: dataset?.name ?? layerId })}
        </div>
      ) : insights.isPending ? (
        <div className="sba-empty" data-insightsloading="">
          {t('insights.loading')}
        </div>
      ) : insights.isError ? (
        <div className="note warn" data-insightserror="">
          {t('insights.failed')}
        </div>
      ) : (
        <InsightsBody data={insights.data} focusIso={focusIso} aoiKm2={aoi?.areaKm2 ?? null} />
      )}
    </section>
  );
}

/**
 * The seven blocks, in the order the client asked for them — *"space out and distinguish
 * each chart and subtitle and title"*.
 *
 * **Categorical** (land cover): key statistics are absent by construction, then the class
 * distribution, the classes stacked per country, the class trend, the watershed ranking and
 * the partnership pie.
 *
 * **Continuous** (soil carbon, rainfall): the key statistics, the four-country means, the
 * mean over time, the watershed ranking and the partnership pie.
 *
 * Each type draws its own country block and its own trend block and never both: the stack
 * *is* the country comparison for a class raster, and printing the modal-class bars beside
 * it would be the same four countries said twice — which is the congestion this task exists
 * to remove. A block the answer does not carry is **absent, not empty**.
 */
function InsightsBody(
  { data, focusIso, aoiKm2 }: { data: Insights; focusIso: string | null; aoiKm2: number | null },
) {
  const { t } = useTranslation();
  const unit = data.layer.unit ?? undefined;
  const categorical = data.type === 'categorical';
  const otherLabel = t('insights.other');

  /** The classes the map is painting, in the map's own order — the shared legend. */
  const legend = data.distribution;
  const stack = useMemo(
    () =>
      categorical
        ? stackedRows(data.countries, legend)
        : { labels: [], series: [], isos: [] },
    [categorical, data.countries, legend],
  );
  const classTrend = useMemo(
    () =>
      categorical && data.trend?.points.length
        ? classTrendSeries(data.trend.points, legend, CLASS_TREND_TOP, otherLabel)
        : { labels: [], series: [] },
    [categorical, data.trend, legend, otherLabel],
  );
  const slices = useMemo(
    () => partnershipSlices(data.partnerships, otherLabel),
    [data.partnerships, otherLabel],
  );

  /**
   * The country block's one concession to the filter — see `focusIso`.
   *
   * `BarsH` reads this **positionally**, so it has to be indexed by the rows the chart is
   * actually drawing. The two blocks no longer agree on what those are: the continuous bars
   * draw every country the server answered for, while the stack drops one whose composition
   * could not be computed. Building both from `data.countries` faded the row that happened to
   * share the dropped country's index — the filter highlighting the wrong country, which is
   * worse than not highlighting at all.
   */
  const fade = (isos: readonly string[]) =>
    focusIso ? isos.map((iso) => (iso === focusIso ? 1 : CONTEXT_BAR_OPACITY)) : undefined;
  const stackOpacity = fade(stack.isos);
  const countryOpacity = fade(data.countries.map((c) => c.country));

  const hotspotTitle =
    data.hotspots.level === 'watershed'
      ? t('insights.hotspotsWatershed')
      : t('insights.hotspots', { n: data.hotspots.rows.length });

  /**
   * What the ring's hole says.
   *
   * The count alone was wrong wherever the server folded a tail: it printed `8 partnerships`
   * over a pie in which a grey wedge held 41 % of the programme, so the number named the
   * *slices* and read as the *partnerships*. Where there is a remainder the centre reports
   * the share the named wedges cover, captioned `Top 8`; where there is none the eight
   * wedges are all of it and the plain count is true.
   */
  const partnershipCenter = (() => {
    const named = data.partnerships?.slices.length ?? 0;
    const other = data.partnerships?.other_pct ?? 0;
    return other > 0
      ? {
          label: `${fmtNum(100 - other, 0)}%`,
          sub: t('insights.partnershipsTop', { n: named }),
        }
      : { label: String(named), sub: t('insights.partnershipsCenter') };
  })();

  /** A multi-line class trend where the epochs carry a composition; the mean line if not. */
  const showClassTrend = classTrend.series.length > 0 && classTrend.labels.length > 1;

  return (
    <div className="ins-body" data-insights={data.layer.id} data-instype={data.type}>
      <div className="ins-extent" data-insextent={data.extent.kind}>
        {/* A drawn extent has no name of its own — the server's `Drawn area` is a placeholder
            in one language — so the line is built here, in the reader's, and carries the area
            because that is the only thing that distinguishes one ring from the next. The
            server's own `area_km2` is preferred over the store's: it is measured on the ring
            the answer was actually computed for. */}
        {data.extent.kind === 'aoi'
          ? t('insights.extentAoi', { km2: fmtNum(data.extent.area_km2 ?? aoiKm2, 1) })
          : data.extent.label ?? t('insights.extentProgramme')}
      </div>

      {/* 1 — key statistics. Continuous only: the mean of a class code is the one number a
          categorical dataset must never show, which is why the server sends null here. */}
      {data.key_stats ? (
        <Block id="key_stats" title={t('insights.keyStats')} sub={t('insights.keyStatsSub')}>
          <StatTiles
            tiles={[
              { label: t('insights.mean'), value: data.key_stats.mean, unit },
              { label: t('insights.median'), value: data.key_stats.median, unit },
              { label: t('insights.min'), value: data.key_stats.min, unit },
              { label: t('insights.max'), value: data.key_stats.max, unit },
              // An aggregated extent has no median to report, and the note under the grid
              // says so in a sentence. A `median —` tile directly above that sentence is the
              // same absence stated twice, the second time as a hole in the grid; the tile
              // goes rather than the explanation. Where the numbers exist, all four stay and
              // the grid is the 2x2 it was designed as.
            ].filter((tile) => !data.key_stats?.aggregated || tile.value !== null)}
          />
          {/* An aggregated extent has no median and no standard deviation to report — see
              `AGGREGATABLE_STATS`. Saying so is the block; printing a blank pair is not. */}
          {data.key_stats.aggregated ? (
            <div className="ins-note" data-insaggregated="">
              {t('insights.aggregated', { n: data.n_zones })}
            </div>
          ) : data.key_stats.std !== null ? (
            <div className="ins-note" data-insstd="">
              {t('insights.spread', {
                std: fmtNum(data.key_stats.std, 1),
                unit: unit ?? '',
              }).trim()}
            </div>
          ) : null}
        </Block>
      ) : null}

      {/* 2 — the class distribution, which is also the legend for blocks 3 and 4. */}
      {data.distribution?.length ? (
        <Block
          id="distribution"
          title={t('insights.distribution')}
          sub={t('insights.distributionSub')}
        >
          <ClassBars rows={data.distribution.slice(0, MAX_CLASSES)} />
        </Block>
      ) : null}

      {/* 3a — every class of every country, stacked (client item 6). */}
      {categorical && stack.series.length ? (
        <Block
          id="classes_by_country"
          title={t('insights.classesByCountry')}
          sub={t('insights.classesByCountrySub')}
          extra={{ 'data-insfocus': focusIso ?? '' }}
        >
          <ClassStack
            labels={stack.labels}
            series={stack.series}
            width={CHART_WIDTH}
            rowOpacity={stackOpacity}
            ariaLabel={t('insights.countriesAria', { name: data.layer.name })}
          />
          {focusIso ? (
            <div className="ins-note" data-inscountriesnote="context">
              {t('insights.countriesContext')}
            </div>
          ) : null}
        </Block>
      ) : null}

      {/* 3b — the continuous country comparison. Always the four, whatever the extent — see
          `focusIso`, which is how the block shows the filter without shrinking to it. */}
      {!categorical && data.countries.length ? (
        <Block
          id="countries"
          title={t('insights.countries')}
          sub={t('insights.countriesSub')}
          extra={{ 'data-insfocus': focusIso ?? '' }}
        >
          <BarsH
            labels={data.countries.map((c) => c.label)}
            series={[
              {
                key: 'countries',
                label: unit ?? t('insights.mean'),
                color: BAR_COLOR,
                values: data.countries.map((c) => c.value),
              },
            ]}
            rowOpacity={countryOpacity}
            w={CHART_WIDTH}
            rowH={22}
            labelGutter={74}
            labelSize={9.5}
            unit={unit ?? undefined}
            fmt={1}
            ariaLabel={t('insights.countriesAria', { name: data.layer.name })}
          />
          {focusIso ? (
            <div className="ins-note" data-inscountriesnote="context">
              {t('insights.countriesContext')}
            </div>
          ) : null}
        </Block>
      ) : null}

      {/* 4 — change over time. One line per class for a class raster (client item 7), the
          mean over the epochs for a continuous one. */}
      {data.trend?.omitted ? (
        <Block id="trend" title={t('insights.classTrend')}>
          <div className="ins-note" data-instrendomitted={data.trend.omitted}>
            {t('insights.trendOmitted')}
          </div>
        </Block>
      ) : showClassTrend ? (
        <Block
          id="class_trend"
          title={t('insights.classTrend')}
          sub={t('insights.classTrendSub')}
        >
          <ClassTrend
            labels={classTrend.labels}
            series={classTrend.series}
            width={CHART_WIDTH}
            ariaLabel={t('insights.trendAria', { name: data.layer.name })}
          />
        </Block>
      ) : data.trend && data.trend.points.length > 1 ? (
        <Block
          id="trend"
          title={t('insights.classTrend')}
          sub={t(
            data.trend.kind === 'epoch' ? 'insights.trendByYear' : 'insights.trendByPeriod',
          )}
        >
          <Spark
            series={data.trend.points.map((p) => p.value)}
            labels={data.trend.points.map((p) => p.year ?? p.period ?? '')}
            w={CHART_WIDTH}
            h={56}
            activeIdx={data.trend.points.length - 1}
            unit={categorical ? '%' : (unit ?? undefined)}
            fmt={1}
            ariaLabel={t('insights.trendAria', { name: data.layer.name })}
          />
        </Block>
      ) : null}

      {/* 5 — hotspots, ranked by watershed (client item 4). A ranking, not a threshold:
          "hotspot" here is "where is this highest", and a cut-off would need a per-dataset
          value nobody has agreed.

          Empty is a *state*, not an absence. The two rankings below are asked of trees the
          admin cascade does not contain, so a perfectly legitimate filter can overlap none of
          them — Kadiogo (BF13) is inside no intervention watershed, and the block used to
          disappear from under the reader with nothing saying why. A one-line empty state says
          the thing the missing block could not, and keeps the panel's block order stable for
          the reader and for the specs that read `data-insblock`. */}
      <Block
        id="hotspots"
        title={hotspotTitle}
        sub={data.hotspots.rows.length
          ? t('insights.hotspotsNote', { n: data.hotspots.n_units })
          : undefined}
      >
        {data.hotspots.rows.length ? (
          <HotspotList hotspots={data.hotspots} />
        ) : (
          <p className="ins-empty" data-insempty="hotspots">{t('insights.hotspotsNone')}</p>
        )}
      </Block>

      {/* 6 — the same question asked of the programme's own partnerships (client item 5). */}
      <Block
        id="partnerships"
        title={t('insights.partnerships')}
        sub={slices.length ? t('insights.partnershipsSub') : undefined}
      >
        {slices.length ? (
          <PartnershipPie
            slices={slices}
            size={DONUT_SIZE}
            centerLabel={partnershipCenter.label}
            centerSub={partnershipCenter.sub}
            ariaLabel={t('insights.partnershipsAria', { name: data.layer.name })}
          />
        ) : (
          <p className="ins-empty" data-insempty="partnerships">
            {t('insights.partnershipsNone')}
          </p>
        )}
      </Block>

      {/* §E7's provenance line: what was read, at what resolution. A panel that prints a
          number must be able to say where it came from. */}
      <div className="ins-source" data-inssource="">
        {t('insights.source', {
          name: data.layer.name,
          version: data.layer.version,
          m: fmtNum(data.resolution_m, 0),
        })}
      </div>
    </div>
  );
}

export default InsightsPanel;
